import React, { PropsWithChildren } from "react";
//#region types
import { WintFormFieldLayoutProps } from "models/propTypes/wintFormPropsTypes";
import { DisplayType } from "models/enums/style";
//#endregion
//#region components
import { Grid } from "@mui/material";
import MessageBubble from "../../messages/messageBubble";
import AutoGeneratedMessage from "../../messages/autoGeneratedMessage";
import InlineLabel from "./inlineLabel";
import Description from "./description";
import Label from "./label";
//#endregion
import { FormHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const FormFieldLayout: React.FC<PropsWithChildren<WintFormFieldLayoutProps>> = (props) => {
  const {
    className,
    direction,
    autogeneratedText,
    label,
    controlLabel,
    descriptionText,
    hasError,
    error,
    customMessage,
    noOuterSpacing,
    hideMainLabel,
    mainLabelGridSize,
    childGridSize,
    hideMessageBubble,
    messageBubblePlacement,
    stackFieldContent,
    verticalAlignCenter,
    onContainerFocus,
    onContainerBlur,
  } = props;

  const gridDirection = direction === "horizontal" ? "row" : "column";

  let message: string | React.ReactElement | undefined = "";
  let messageType: DisplayType = DisplayType.None;

  if (hasError) {
    // Flatten nested (array/object) errors to the first leaf message so those fields surface text too.
    const errorText = typeof error === "string" ? error : FormHelper.getErrorMessages(error)[0];
    if (errorText) {
      message = LocalizationHelperInstance.getFormattedMessage(errorText, errorText);
      messageType = DisplayType.Danger;
    }
  } else if (customMessage) {
    message = customMessage.message;
    messageType = customMessage.type;
  }

  const actualLabel = typeof label === "string" ? LocalizationHelperInstance.getFormattedMessage(label, label) : label;
  const actualControlLabel =
    typeof controlLabel === "string"
      ? LocalizationHelperInstance.getFormattedMessage(controlLabel, controlLabel)
      : controlLabel;

  const actualDescriptionText =
    typeof descriptionText === "string"
      ? LocalizationHelperInstance.getFormattedMessage(descriptionText, descriptionText)
      : descriptionText;

  // The message bubble's invisible anchor is sized to match how it's positioned: a 0-width
  // vertical line at the field's right edge for "right"-ish placements, or a 0-height line
  // spanning the field's full width for "top"-ish placements (so a centered/aligned tooltip
  // has the right span to center or align against). For the horizontal layout this means the
  // bubble has to stack *above* the field (its own row) rather than sit *beside* it — the two
  // anchor shapes aren't interchangeable within the same row.
  const messageBubbleIsTop = (messageBubblePlacement ?? "right").startsWith("top");

  return (
    <Grid
      container
      item
      onFocus={onContainerFocus}
      onBlur={onContainerBlur}
      spacing={direction === "horizontal" ? 2 : 1}
      direction={gridDirection}
      className={clsx(
        noOuterSpacing && className !== undefined ? className : noOuterSpacing ? "" : cssExports.formControl,
        className
      )}
    >
      {direction === "horizontal" ? (
        <>
          {!hideMainLabel && (
            <InlineLabel
              descriptionText={actualDescriptionText}
              columns={mainLabelGridSize}
              verticalAlignCenter={verticalAlignCenter && !actualDescriptionText}
            >
              {actualControlLabel ?? actualLabel}
            </InlineLabel>
          )}
          <Grid
            item
            container
            xs={childGridSize ?? 5}
            direction="column"
            justifyContent={verticalAlignCenter ? "center" : undefined}
            className={clsx(
              cssExports.formControlLabelContainer,
              messageBubbleIsTop && cssExports.formFieldMessageAttachElemTop
            )}
          >
            {messageBubbleIsTop && !hideMessageBubble && (
              <MessageBubble placement={messageBubblePlacement ?? "top"} message={message} type={messageType} />
            )}
            <Grid
              container
              direction="row"
              className={!messageBubbleIsTop ? cssExports.formFieldMessageAttachElemRight : undefined}
            >
              <Grid container item xs direction={stackFieldContent ? "column" : "row"}>
                {props.children}
                {hideMainLabel && descriptionText && <Description horizontal>{actualDescriptionText}</Description>}
              </Grid>
              {!messageBubbleIsTop && !hideMessageBubble && (
                <MessageBubble placement={messageBubblePlacement ?? "right"} message={message} type={messageType} />
              )}
            </Grid>
          </Grid>
        </>
      ) : (
        <Grid
          item
          container
          xs
          direction="column"
          className={clsx(cssExports.formControlLabelContainer, cssExports.formFieldMessageAttachElemTop)}
        >
          {!hideMainLabel && (
            <Grid item>
              <Label>{actualControlLabel ?? actualLabel}</Label>
            </Grid>
          )}
          {!hideMessageBubble && (
            <MessageBubble placement={messageBubblePlacement ?? "top"} message={message} type={messageType} />
          )}
          <Grid item>{props.children}</Grid>
          {descriptionText && <Description>{actualDescriptionText}</Description>}
        </Grid>
      )}
      {!UtilsHelper.isEmpty(autogeneratedText) && (
        <Grid item xs={direction === "horizontal" ? 4 : undefined}>
          <Grid
            container
            direction="column"
            justifyContent="center"
            className={cssExports.autoGeneratedMessageContainer}
          >
            <AutoGeneratedMessage>
              {LocalizationHelperInstance.getFormattedMessage(autogeneratedText!, autogeneratedText)}
            </AutoGeneratedMessage>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default FormFieldLayout;
